import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { ReactComponent as LogoSvg } from 'src/assets/logoS360.svg';
import {
  Attachment,
  Button,
  FormikCalendar,
  FormikInputPhone,
  FormikInputSelect,
  FormikInputSelectMulti,
  FormikInputString,
  FormikInputZip,
  FormikRadioGroup,
  H1,
  H3,
  Icon,
  InputSelectMulti,
  P1M,
  P1R,
  Loader,
} from 'src/components';
import { themeColors } from 'src/theme';
import { useFormik } from 'formik';
import { enumToTranslatedOptions, normalizerTimestamp } from 'src/utils';
import config from 'src/config';
import { loadUser, saveProfile } from 'src/redux/user/actions';
import * as Yup from 'yup';
import { validation } from 'src/utils/validation';
import { RubberSleeveSize } from 'src/shared/enums/rubberSleeveSize.enum';
import { EmergencyContactRelationship } from 'src/shared/enums/emergencyContactRelationship.enum';
import { SyntheticEvent, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectUser, selectSavingProfile, selectSaveProfileSuccess } from 'src/redux/user/selectors';
import { EmploymentTypeEnum } from 'src/shared/enums/employmentType.enum';
import { ListType } from 'src/types';
import { useNavigate } from 'react-router-dom';
import { PATHS } from 'src/navigation';
import { getUnionClassificationOptions, parseTimestampFromDB, timestampToDBFormat } from 'src/components/UserProfile/helpers';
import { ResponsiveGridBox, SingleColumnGridBox, StyledSectionBox } from './styled';

export type FormValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  prefferedName: string;
  phone: string;
  streetAddress: string;
  streetAddress2: string;
  city: string;
  state: string;
  zipcode: string;
  country: string;
  contactFirstName: string;
  contactLastName: string;
  contactPhone: string;
  contactEmail: string;
  contactRelationship: string;
  email: string;
  roles: string[];
  ssn: string;
  preferredName: string;
  rubberGlove: string;
  rubberSleeve: string;
  workGlove: string;
  workVest: string;
  localUnion: string;
  unionTicket: string;
  employmentType: EmploymentTypeEnum;
  unionClassification: string | number;
  driverLicenseExpiration?: number | null;
  cdlEndorsements: (string | undefined)[];
  cdlRestrictions: (string | undefined)[];
  dateOfBirth: number | null;
};
function ConfirmUserDataContainer() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [activeUpload, setActiveUpload] = useState<null | string>(null);
  const user = useSelector(selectUser);
  const isSaving = useSelector(selectSavingProfile);
  const saveSuccess = useSelector(selectSaveProfileSuccess);

  useEffect(() => {
    if (!user.uuid) {
      dispatch(loadUser.init());
    } else if (user.dataApproved || saveSuccess) {
      navigate(PATHS.MY_PROFILE_PAGE, { replace: true });
    }
  }, [user, saveSuccess]);

  const roles = config.options.roles
    .map((role) => ({ key: role.slug, value: role.name }))
    .concat(user.currentRole === 'NEW_USER' ? [{ key: 'NEW_USER', value: 'New User' }] : []);

  const initialValues: FormValues = {
    firstName: user.firstName || '',
    middleName: user.middleName || '',
    dateOfBirth: (user.dateOfBirth && parseTimestampFromDB(user.dateOfBirth)) || null,
    lastName: user.lastName || '',
    prefferedName: user.prefferedName || '',
    phone: user.phone || '',
    streetAddress: user.address?.streetAddress || '',
    streetAddress2: user.address?.streetAddress2 || '',
    city: user.address?.city || '',
    state: user.address?.state || '',
    zipcode: user.address?.zipcode || '',
    country: user.address?.country || '',
    contactFirstName: user.emergencyContact?.contactFirstName || '',
    contactLastName: user.emergencyContact?.contactLastName || '',
    contactPhone: user.emergencyContact?.contactPhone || '',
    contactEmail: user.emergencyContact?.contactEmail || '',
    contactRelationship: user.emergencyContact?.contactRelationship || '',

    email: user.email || '',
    roles: user.roles?.map((role) => role.slug) || [],

    preferredName: user.preferredName || '',
    unionClassification: user.unionDetails?.unionClassification || '',
    ssn: user.ssn || '',
    localUnion: user.unionDetails?.localUnion || '',
    unionTicket: user.unionDetails?.unionTicket || '',
    employmentType: user.unionDetails?.employmentType || EmploymentTypeEnum.UNION,

    rubberSleeve: user.ppeDetails?.rubberSleeve || '',
    workVest: user.ppeDetails?.workVest || '',
    workGlove: user.ppeDetails?.workGlove || '',
    rubberGlove: user.ppeDetails?.rubberGlove || '',

    driverLicenseExpiration: (user.unionDetails?.driverLicenseExpiration && normalizerTimestamp(user?.unionDetails?.driverLicenseExpiration)) || null,

    cdlEndorsements: (user.unionDetails?.cdlEndorsements || []).map((item) => item) || [],
    cdlRestrictions: (user.unionDetails?.cdlRestrictions || []).map((item) => item) || [],
  };

  const validationSchema = Yup.object({
    email: Yup.string().matches(validation.email, 'Invalid email').required('Email Cannot Be Empty'),
    phone: Yup.string().max(13).matches(validation.phonePlusOne, 'Invalid phone').notRequired(),
    firstName: Yup.string().min(3).max(64).required('First Name Cannot Be Empty'),
    lastName: Yup.string().min(3).max(64).required('Last Name Cannot Be Empty'),
    middleName: Yup.string().min(3).max(64).notRequired(),
    preferredName: Yup.string().min(3).max(64).notRequired(),
    ssn: Yup.string().matches(validation.ssn, 'Invalid SSN').notRequired(),
    zipcode: Yup.string().matches(validation.zipCode, 'Invalid ZIP').notRequired(),
    emergencyEmail: Yup.string().email().max(128).notRequired(),
    emergencyPhone: Yup.string().max(13).matches(validation.phonePlusOne, 'Invalid emergency phone').notRequired(),
    emergencyFirstName: Yup.string().min(3).max(64).notRequired(),
    emergencyLastName: Yup.string().min(3).max(64).notRequired(),
    emergencyRelationship: Yup.string().oneOf(Object.values(EmergencyContactRelationship)).notRequired(),
    employmentType: Yup.number()
      .oneOf(Object.values(EmploymentTypeEnum).filter((v) => typeof v === 'number') as number[])
      .notRequired(),
    country: Yup.string().max(64).notRequired(),
    city: Yup.string().max(64).notRequired(),
    region: Yup.string().notRequired(),
    state: Yup.string().max(64).notRequired(),
    streetAddress: Yup.string().max(64).notRequired(),
    streetAddress2: Yup.string().max(64).notRequired(),
    dateOfBirth: Yup.number().notRequired(),
    driverLicenseExpiration: Yup.number().notRequired(),
    localUnion: Yup.string().notRequired(),
    unionTicket: Yup.string().notRequired(),
    unionClassification: Yup.string().max(64).notRequired(),
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (data) => {
      const userData = {
        firstName: data.firstName,
        middleName: data.middleName,
        lastName: data.lastName,
        prefferedName: data.prefferedName,
        preferredName: data.preferredName,
        dateOfBirth: data.dateOfBirth ? timestampToDBFormat(data.dateOfBirth as number) : null,

        email: data.email,
        phone: data.phone,

        address: {
          streetAddress: data.streetAddress || '',
          streetAddress2: data.streetAddress2 || '',
          city: data.city || '',
          state: data.state || '',
          zipcode: data.zipcode || '',
          country: data.country || '',
        },

        emergencyContact: {
          contactFirstName: data.contactFirstName || '',
          contactLastName: data.contactLastName || '',
          contactPhone: data.contactPhone || '',
          contactEmail: data.contactEmail || '',
          contactRelationship: data.contactRelationship || '',
        },

        unionDetails: {
          unionClassification: data.unionClassification || '',
          localUnion: data.localUnion || '',
          unionTicket: data.unionTicket || '',
          employmentType: data.employmentType || '',
          driverLicenseExpiration: data.driverLicenseExpiration || null,
          cdlEndorsements: (data.cdlEndorsements || []).map((item) => item),
          cdlRestrictions: (data.cdlRestrictions || []).map((item) => item),
        },

        ppeDetails: {
          workVest: data.workVest || '',
          workGlove: data.workGlove || '',
          rubberGlove: data.rubberGlove || '',
          rubberSleeve: data.rubberSleeve || '',
        },

        dataApproved: true,
      };

      dispatch(saveProfile.init({ ...userData, uuid: user.uuid }));
      navigate(PATHS.MY_PROFILE_PAGE, { replace: true });
    },
  });

  const { country } = formik.values;
  const states = country ? (country === 'Canada' ? config.options.STATES.CA : config.options.STATES.US) : [];

  const handleSubmit = () => {
    formik.handleSubmit();
  };

  const handleActiveUpload = (uploadName: string) => {
    setActiveUpload(uploadName);
  };

  return (
    <Box sx={{ pb: '85px' }}>
      <Box sx={{ px: 'clamp(15px, 5vw, 85px)', py: '35px' }} component="header">
        <LogoSvg />
      </Box>

      <Box sx={{ width: '100%' }} component="main">
        <Box sx={{ maxWidth: '630px', margin: '0 auto' }}>
          <H1 sx={{ textAlign: 'center', marginBottom: '35px', px: 1 }}>{t('confirmDataPage.title')}</H1>
          <Box sx={{ mb: '40px', p: '25px', gap: '10px', backgroundColor: themeColors.lightYellow, display: 'flex', alignItems: 'center' }}>
            <Icon name="dangerAlert" />
            <Box>
              <P1R>
                {t('confirmDataPage.reviewInformation')}
                <br />
                <P1M component="span">{t('confirmDataPage.uploadId')}</P1M>
              </P1R>
            </Box>
          </Box>

          <Box sx={{ px: 1, display: 'flex', flexDirection: 'column', gap: '24px', position: 'relative', width: '100%', maxWidth: '630px' }}>
            {isSaving ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
                <Loader />
              </Box>
            ) : (
              <>
                <StyledSectionBox>
                  <H3 component="h3" sx={{ marginBottom: '36px' }}>
                    {t('confirmDataPage.account')}
                  </H3>

                  <ResponsiveGridBox>
                    <FormikInputString formik={formik} field="firstName" label={t('profilePage.legalFirstName')} />
                    <FormikInputString formik={formik} field="middleName" label={t('profilePage.middleName')} />
                    <FormikInputString formik={formik} field="lastName" label={t('profilePage.legalLastName')} />
                    <FormikInputPhone formik={formik} field="phone" label={t('profilePage.phone')} />
                    <FormikInputString formik={formik} field="ssn" label={t('profilePage.ssn')} disabled />
                    <FormikCalendar formik={formik} field="dateOfBirth" label={t('profilePage.dateOfBirth')} />
                  </ResponsiveGridBox>
                </StyledSectionBox>

                <StyledSectionBox>
                  <H3 component="h3" sx={{ marginBottom: '36px' }}>
                    {t('confirmDataPage.preferredName')}
                  </H3>

                  <ResponsiveGridBox>
                    <FormikInputString formik={formik} field="preferredName" label={t('profilePage.preferredName')} />
                  </ResponsiveGridBox>
                </StyledSectionBox>

                <StyledSectionBox>
                  <H3 component="h3" sx={{ marginBottom: '36px' }}>
                    {t('confirmDataPage.roles')}
                  </H3>
                  <SingleColumnGridBox>
                    <FormikInputSelectMulti formik={formik} field="roles" options={roles} label={t('profilePage.roles')} disabled readOnly />
                  </SingleColumnGridBox>
                </StyledSectionBox>

                <StyledSectionBox>
                  <H3 component="h3" sx={{ marginBottom: '36px' }}>
                    {t('confirmDataPage.address')}
                  </H3>

                  <ResponsiveGridBox>
                    <FormikInputSelect
                      formik={formik}
                      field="country"
                      label={t('profilePage.country')}
                      options={config.options.COUNTRY.map((country) => ({ key: country, value: country }))}
                    />
                    <FormikInputString formik={formik} field="streetAddress" label={t('profilePage.streetAddress')} />
                    <FormikInputString formik={formik} field="streetAddress2" label={t('profilePage.streetAddress2')} />
                    <FormikInputString formik={formik} field="city" label={t('profilePage.city')} />
                    <FormikInputSelect
                      formik={formik}
                      field="state"
                      label={country === 'Canada' ? 'Province' : t('profilePage.state')}
                      options={states.map((state) => ({ key: state, value: t(`${country === 'Canada' ? 'provinces' : 'states'}.${state}`) }))}
                    />
                    <FormikInputZip formik={formik} field="zipcode" label={t('profilePage.zipcode')} />
                  </ResponsiveGridBox>
                </StyledSectionBox>

                <StyledSectionBox>
                  <H3 component="h3" sx={{ marginBottom: '36px' }}>
                    {t('confirmDataPage.id')}
                  </H3>

                  <SingleColumnGridBox>
                    <Attachment
                      title={t('profilePage.idFront')}
                      typeName="id_front"
                      userId={user.uuid}
                      key={user.attachments?.id_front?.id}
                      selectedAttachment={user.attachments?.id_front}
                      activeField={activeUpload}
                      handleActiveUpload={handleActiveUpload}
                    />
                    <Attachment
                      title={t('profilePage.idBack')}
                      typeName="id_back"
                      userId={user.uuid}
                      key={user.attachments?.id_back?.id}
                      selectedAttachment={user.attachments?.id_back}
                      activeField={activeUpload}
                      handleActiveUpload={handleActiveUpload}
                    />
                  </SingleColumnGridBox>
                </StyledSectionBox>

                <StyledSectionBox>
                  <H3 component="h3" sx={{ marginBottom: '36px' }}>
                    {t('confirmDataPage.emergencyContact')}
                  </H3>

                  <ResponsiveGridBox>
                    <FormikInputString formik={formik} field="contactFirstName" label={t('profilePage.firstName')} />
                    <FormikInputString formik={formik} field="contactLastName" label={t('profilePage.lastName')} />
                    <FormikInputPhone formik={formik} field="contactPhone" label={t('profilePage.phone')} />
                    <FormikInputString
                      formik={formik}
                      field="contactEmail"
                      label={t('profilePage.email')}
                      onChange={(e) => {
                        const value = e.target.value || '';
                        formik.setFieldValue('contactEmail', value.toLowerCase());
                      }}
                    />
                    <FormikInputSelect
                      formik={formik}
                      field="contactRelationship"
                      label={t('profilePage.relationship')}
                      options={config.options.emergencyContactRelationships.map((rel) => ({ key: rel.value, value: rel.value }))}
                    />
                  </ResponsiveGridBox>
                </StyledSectionBox>

                <StyledSectionBox>
                  <H3 component="h3" sx={{ marginBottom: '36px' }}>
                    {t('confirmDataPage.ppe')}
                  </H3>

                  <ResponsiveGridBox>
                    <FormikInputSelect
                      formik={formik}
                      field="rubberSleeve"
                      label={t('profilePage.rubberSleeve')}
                      options={enumToTranslatedOptions(RubberSleeveSize, t, 'RubberSleeveSize')}
                    />
                    <FormikInputSelect
                      formik={formik}
                      field="rubberGlove"
                      label={t('profilePage.rubberGlove')}
                      options={config.options.rubberGloveSize}
                    />
                    <FormikInputSelect formik={formik} field="workVest" label="Work Vest" options={config.options.workVestSize} />
                    <FormikInputSelect formik={formik} field="workGlove" label={t('profilePage.workGlove')} options={config.options.workGloveSize} />
                  </ResponsiveGridBox>
                </StyledSectionBox>

                <StyledSectionBox>
                  <H3 component="h3" sx={{ marginBottom: '36px' }}>
                    {t('confirmDataPage.unionClass')}
                  </H3>

                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <P1R sx={{ mr: { xs: 2, sm: 4 } }}>{t('profilePage.employmentTypeTitle')}</P1R>
                    <FormikRadioGroup
                      formik={formik}
                      field="employmentType"
                      options={[
                        { value: EmploymentTypeEnum.UNION, label: t('profilePage.employmentTypeUnion') },
                        { value: EmploymentTypeEnum.NON_UNION, label: t('profilePage.employmentTypeNonUnion') },
                      ]}
                      onChange={(event) => {
                        formik.setFieldValue('employmentType', Number(event.target.value));
                        formik.setFieldValue('unionClassification', null);
                        formik.setFieldValue('localUnion', null);
                        formik.setFieldValue('unionTicket', '');
                      }}
                    />
                  </Box>
                  {formik.values.employmentType === EmploymentTypeEnum.UNION && (
                    <>
                      <ResponsiveGridBox>
                        <FormikInputSelect
                          formik={formik}
                          field="localUnion"
                          label={t('profilePage.localUnion')}
                          options={
                            config.options.UNIONS?.map((classification: string) => ({
                              key: classification,
                              value: t(`unions.${classification}`),
                            })) || []
                          }
                        />
                        <FormikInputString formik={formik} field="unionTicket" label="Union Ticket #" />
                      </ResponsiveGridBox>
                      <Attachment
                        title={t('profilePage.unionTicket')}
                        typeName="union"
                        userId={user.uuid}
                        key={user.attachments?.union?.id}
                        selectedAttachment={user.attachments?.union}
                        activeField={activeUpload}
                        handleActiveUpload={handleActiveUpload}
                      />
                    </>
                  )}

                  <ResponsiveGridBox>
                    <FormikInputSelect
                      formik={formik}
                      field="unionClassification"
                      label={t('profilePage.classification')}
                      options={getUnionClassificationOptions(formik.values.employmentType, formik.values.localUnion, t)}
                    />
                    <FormikCalendar formik={formik} field="driverLicenseExpiration" label={t('profilePage.cdlExpirationDate')} />
                    <InputSelectMulti
                      name="cdlEndorsements"
                      label={t('profilePage.cdlEndorsements')}
                      placeholder="Select"
                      getValue={() => formik.values.cdlEndorsements}
                      options={config.options.CDL_ENDORSEMENTS?.map((endorsement) => ({
                        key: endorsement,
                        value: t(`cdlEndorsements.${endorsement}`),
                      }))}
                      onChange={(event: SyntheticEvent, value: ListType[]) => {
                        formik.setFieldValue(
                          'cdlEndorsements',
                          value.map((item) => item?.key),
                        );

                        if (value[value.length - 1]?.key === 'N_A') {
                          formik.setFieldValue('cdlEndorsements', ['N_A']);
                          formik.setFieldValue('cdlRestrictions', ['N_A']);
                        } else if (value[value.length - 1]?.key !== 'N_A') {
                          formik.setFieldValue(
                            'cdlEndorsements',
                            value.filter((item) => item?.key !== 'N_A').map((item) => item?.key),
                          );

                          formik.setFieldValue(
                            'cdlRestrictions',
                            value.filter((item) => item?.key !== 'N_A').map((item) => item?.key),
                          );
                        }
                      }}
                      error={formik.touched.cdlEndorsements && Boolean(formik.errors.cdlEndorsements)}
                      helperText={formik.touched.cdlEndorsements && formik.errors.cdlEndorsements}
                      sx={{ '& .MuiInputBase-root': { background: 'white' } }}
                    />
                    <InputSelectMulti
                      name="cdlRestrictions"
                      label={t('profilePage.cdlRestrictions')}
                      placeholder="Select"
                      disabled={formik.values.cdlEndorsements.includes('N_A')}
                      isChipDisabled={() => formik.values.cdlEndorsements.includes('N_A')}
                      getValue={() => formik.values.cdlRestrictions}
                      options={config.options.CDL_RESTRICTIONS?.map((restriction) => ({
                        key: restriction,
                        value: t(`cdlRestrictions.${restriction}`),
                      }))}
                      onChange={(_, value: ListType[]) => {
                        formik.setFieldValue(
                          'cdlRestrictions',
                          value.map((item) => item?.key),
                        );

                        if (value[value.length - 1]?.key === 'N_A') {
                          formik.setFieldValue('cdlRestrictions', ['N_A']);
                        } else if (value[value.length - 1]?.key !== 'N_A') {
                          formik.setFieldValue(
                            'cdlRestrictions',
                            value.filter((item) => item?.key !== 'N_A').map((item) => item?.key),
                          );
                        }
                      }}
                      error={formik.touched.cdlRestrictions && Boolean(formik.errors.cdlRestrictions)}
                      helperText={formik.touched.cdlRestrictions && formik.errors.cdlRestrictions}
                      sx={{ '& .MuiInputBase-root': { background: 'white' } }}
                    />
                  </ResponsiveGridBox>
                  <SingleColumnGridBox>
                    <Attachment
                      title={t('profilePage.driverLicenseFront')}
                      typeName="driver_license_front"
                      userId={user.uuid}
                      key={user.attachments?.driver_license_front?.id}
                      selectedAttachment={user.attachments?.driver_license_front}
                      activeField={activeUpload}
                      handleActiveUpload={handleActiveUpload}
                    />
                    <Attachment
                      title={t('profilePage.driverLicenseBack')}
                      typeName="driver_license_back"
                      userId={user.uuid}
                      key={user.attachments?.driver_license_back?.id}
                      selectedAttachment={user.attachments?.driver_license_back}
                      activeField={activeUpload}
                      handleActiveUpload={handleActiveUpload}
                    />
                  </SingleColumnGridBox>
                </StyledSectionBox>
              </>
            )}

            <Box sx={{ position: 'fixed', bottom: '15px', left: '50%', transform: 'translateX(-50%)', width: '100%', maxWidth: '630px', px: 1 }}>
              <Button onClick={handleSubmit} fullWidth disabled={isSaving}>
                {isSaving ? t('common.saving') : 'Confirm & Submit'}
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
}

export default ConfirmUserDataContainer;
